// ============================================================
// LawParamsPanel — 門店設定 →「法規數字」
// 基本工資、勞健保費率不再寫死在程式裡：政府改了，老闆在這裡改，
// 從選定的月份起生效；已結算的月份不會變。
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';

interface LawRates {
  minWageMonthly: number;
  minWageHourly: number;
  laborInsRate: number;
  employmentInsRate: number;
  healthInsRate: number;
  laborWorkerShare: number;
  healthWorkerShare: number;
  effectiveFrom: Record<string, string>;
}

interface LawRow { id: string; key: string; value: number; effectiveFrom: string; note: string | null }

interface LawData {
  thisMonth: string;
  current: LawRates;
  next: LawRates;
  rows: LawRow[];
  latestReport: { checkedAt: string; hasChange: boolean; summary: string } | null;
  earliestEditableMonth: string;
}

type Field = keyof Omit<LawRates, 'effectiveFrom'>;

// 欄位順序、顯示名稱、對應後端 key、是否為百分比
const FIELDS: { field: Field; key: string; label: string; percent: boolean }[] = [
  { field: 'minWageMonthly', key: 'MIN_WAGE_MONTHLY', label: '基本工資（月薪）', percent: false },
  { field: 'minWageHourly', key: 'MIN_WAGE_HOURLY', label: '基本工資（時薪）', percent: false },
  { field: 'laborInsRate', key: 'LABOR_INS_RATE', label: '勞保費率', percent: true },
  { field: 'employmentInsRate', key: 'EMPLOYMENT_INS_RATE', label: '就業保險費率', percent: true },
  { field: 'healthInsRate', key: 'HEALTH_INS_RATE', label: '健保費率', percent: true },
  { field: 'laborWorkerShare', key: 'LABOR_INS_WORKER_SHARE', label: '勞保員工負擔比例', percent: true },
  { field: 'healthWorkerShare', key: 'HEALTH_INS_WORKER_SHARE', label: '健保員工負擔比例', percent: true },
];

const show = (v: number, percent: boolean) =>
  percent ? `${Math.round(v * 10000) / 100}%` : `$${v.toLocaleString()}`;

const LABEL_BY_KEY = Object.fromEntries(FIELDS.map(f => [f.key, f]));

function monthsFrom(start: string, n: number): string[] {
  const [y, m] = start.split('-').map(Number);
  return Array.from({ length: n }, (_, i) => {
    const idx = y * 12 + (m - 1) + i;
    return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`;
  });
}

export function LawParamsPanel({ isOwner }: { isOwner: boolean }) {
  const [data, setData] = useState<LawData | null>(null);
  const [editing, setEditing] = useState(false);
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.get<LawData>('/law-params'));
    } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    if (!data) return;
    // 預設下個月；若下個月已結算（不太可能）就用最早可改的月份
    const nextMonth = monthsFrom(data.thisMonth, 2)[1];
    setEffectiveFrom(data.earliestEditableMonth > nextMonth ? data.earliestEditableMonth : nextMonth);
    setInputs(Object.fromEntries(FIELDS.map(f => [
      f.key,
      f.percent ? String(Math.round(data.next[f.field] * 10000) / 100) : String(data.next[f.field]),
    ])));
    setNote('');
    setEditing(true);
  };

  const save = async () => {
    if (!data) return;
    const values: Record<string, number> = {};
    for (const f of FIELDS) {
      const n = Number(inputs[f.key]);
      if (!Number.isFinite(n) || inputs[f.key] === '') { alert(`請填「${f.label}」`); return; }
      values[f.key] = f.percent ? Math.round(n * 100) / 10000 : n;
    }
    const ok = window.confirm(`從 ${effectiveFrom} 起改用這組數字？\n\n${effectiveFrom} 以前的月份不受影響。`);
    if (!ok) return;
    setBusy(true);
    try {
      await api.post('/law-params', { effectiveFrom, values, note: note.trim() || undefined });
      setEditing(false);
      await load();
    } catch (err) {
      alert('儲存失敗：' + (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="bg-white rounded-card shadow-card p-6 text-morandi-300">載入中...</div>;

  const nextMonth = monthsFrom(data.thisMonth, 2)[1];
  const history = Object.entries(
    data.rows.reduce<Record<string, LawRow[]>>((acc, r) => { (acc[r.effectiveFrom] ??= []).push(r); return acc; }, {})
  ).sort((a, b) => b[0].localeCompare(a[0]));

  return (
    <div className="bg-white rounded-card shadow-card p-6 space-y-5">
      <div>
        <h2 className="text-lg font-bold text-morandi-800 mb-1">法規數字</h2>
        <p className="text-sm text-morandi-400">
          政府調整基本工資或勞健保費率時，在這裡改。新數字從你選的月份起算，已結算的月份不會變。
        </p>
      </div>

      {/* 最近一次自動檢查 */}
      <div className={`rounded-btn p-3 text-sm ${data.latestReport?.hasChange ? 'bg-rose-50 text-rose-700' : 'bg-morandi-50 text-morandi-500'}`}>
        {data.latestReport ? (
          <>
            <div className="text-xs mb-1 opacity-70">
              最近一次自動檢查：{new Date(data.latestReport.checkedAt).toLocaleDateString('zh-TW', { timeZone: 'Asia/Taipei' })}
            </div>
            <div className="whitespace-pre-line">{data.latestReport.summary}</div>
          </>
        ) : '還沒有自動檢查紀錄。每月 1 號早上，店務主機會自動上網查法規有沒有改。'}
      </div>

      {/* 目前數字 */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-morandi-400">
              <th className="text-left py-2 font-medium">項目</th>
              <th className="text-right py-2 font-medium">本月（{data.thisMonth}）</th>
              <th className="text-right py-2 font-medium">下個月（{nextMonth}）</th>
            </tr>
          </thead>
          <tbody>
            {FIELDS.map(f => {
              const changed = data.current[f.field] !== data.next[f.field];
              return (
                <tr key={f.key} className="border-t border-morandi-50">
                  <td className="py-2 text-morandi-700">{f.label}</td>
                  <td className="py-2 text-right font-mono text-morandi-600">{show(data.current[f.field], f.percent)}</td>
                  <td className={`py-2 text-right font-mono ${changed ? 'text-earth-600 font-semibold' : 'text-morandi-600'}`}>
                    {show(data.next[f.field], f.percent)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {isOwner && !editing && (
        <button onClick={startEdit}
          className="px-4 py-2 bg-earth-400 hover:bg-earth-500 text-white rounded-btn text-sm">
          修改數字
        </button>
      )}

      {editing && (
        <div className="border border-earth-200 rounded-btn p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-morandi-600">從哪個月起適用</span>
            <select value={effectiveFrom} onChange={e => setEffectiveFrom(e.target.value)}
              className="px-3 py-2 bg-morandi-50 border border-morandi-200 rounded-btn text-sm">
              {monthsFrom(data.earliestEditableMonth, 18).map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="text-xs text-morandi-400">
            {data.earliestEditableMonth} 以前的薪資已結算，不能改。費率請填百分比，例如 11.5。
          </div>
          {FIELDS.map(f => (
            <label key={f.key} className="flex items-center justify-between gap-3">
              <span className="text-sm text-morandi-700">{f.label}</span>
              <span className="flex items-center gap-1">
                {!f.percent && <span className="text-morandi-400 text-sm">$</span>}
                <input type="number" step="any" value={inputs[f.key] ?? ''}
                  onChange={e => setInputs(v => ({ ...v, [f.key]: e.target.value }))}
                  className="w-28 px-3 py-2 bg-morandi-50 border border-morandi-200 rounded-btn text-sm text-right font-mono" />
                {f.percent && <span className="text-morandi-400 text-sm">%</span>}
              </span>
            </label>
          ))}
          <input value={note} onChange={e => setNote(e.target.value)} maxLength={200}
            placeholder="備註（選填，例如：勞動部 9 月公告）"
            className="w-full px-3 py-2 bg-morandi-50 border border-morandi-200 rounded-btn text-sm" />
          <div className="flex justify-end gap-2">
            <button onClick={() => setEditing(false)} className="px-4 py-2 text-sm text-morandi-500 hover:bg-morandi-100 rounded-btn">取消</button>
            <button onClick={save} disabled={busy}
              className="px-4 py-2 bg-earth-400 hover:bg-earth-500 disabled:opacity-50 text-white rounded-btn text-sm">
              {busy ? '儲存中…' : '儲存'}
            </button>
          </div>
        </div>
      )}

      {/* 歷史 */}
      {history.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-morandi-500">過去的數字（{history.length} 組）</summary>
          <div className="mt-2 space-y-3">
            {history.map(([month, rows]) => (
              <div key={month} className="bg-morandi-50 rounded-btn p-3">
                <div className="font-medium text-morandi-700 mb-1">{month === '2000-01' ? '系統舊數字' : `${month} 起`}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 text-xs text-morandi-500">
                  {[...rows].sort((a, b) => FIELDS.findIndex(f => f.key === a.key) - FIELDS.findIndex(f => f.key === b.key)).map(r => {
                    const f = LABEL_BY_KEY[r.key];
                    return f ? <div key={r.id}>{f.label}：{show(r.value, f.percent)}</div> : null;
                  })}
                </div>
                {rows[0]?.note && <div className="text-xs text-morandi-400 mt-1">{rows[0].note}</div>}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
